import type { ConnectorDto } from "~/ui-rtk/api/types/connector.types"
import type { CompanyConnectorDto } from "~/ui-rtk/api/types/company-connector.types"

/**
 * Checks if a company connector's scope version matches the current connector's scope version
 * @param connector - The connector definition with current scope version
 * @param companyConnector - The company connector instance
 * @returns true if versions match or if scope versions are not defined, false if they mismatch
 */
export const isConnectorVersionUpToDate = (
  connector: ConnectorDto | null | undefined,
  companyConnector: CompanyConnectorDto | null | undefined,
): boolean => {
  // If either is missing, assume up to date (backwards compatibility)
  if (!connector || !companyConnector) {
    return true
  }

  // If scope versions are not defined, assume up to date (backwards compatibility)
  if (
    connector.scopeVersion === undefined ||
    companyConnector.scopeVersion === undefined
  ) {
    return true
  }

  // Check if versions match
  return connector.scopeVersion === companyConnector.scopeVersion
}

/**
 * Gets a warning message for version mismatches
 * @param connectorName - Name of the connector
 * @param currentVersion - Current connector scope version
 * @param companyVersion - Company connector scope version
 * @returns warning message string
 */
export const getVersionMismatchWarning = (
  connectorName: string,
  currentVersion?: number,
  companyVersion?: number,
): string =>
  `${connectorName} has been updated to version ${currentVersion || "unknown"}. Your connection is using version ${companyVersion || "unknown"}. Upgrade now to access new features, improved performance, and the latest bug fixes.`

/**
 * Checks if a connector should show reconnect status due to version mismatch
 * @param connector - The connector definition
 * @param companyConnector - The company connector instance
 * @param currentStatus - The current status of the company connector
 * @returns true if should show reconnect status
 */
export const shouldShowReconnectForVersion = (
  connector: ConnectorDto | null | undefined,
  companyConnector: CompanyConnectorDto | null | undefined,
  currentStatus: string,
): boolean => {
  // Only check version for connected connectors
  if (currentStatus !== "CONNECTED") {
    return false
  }

  // Check if version is outdated
  return !isConnectorVersionUpToDate(connector, companyConnector)
}

/**
 * Extracts version information from connector and company connector for display
 * @param connector - The connector definition
 * @param companyConnector - The company connector instance
 * @returns object with current and company scope versions
 */
export const extractVersionInfo = (
  connector: ConnectorDto | null | undefined,
  companyConnector: CompanyConnectorDto | null | undefined,
): {
  currentScopeVersion?: number
  companyScopeVersion?: number
  hasVersionMismatch: boolean
} => {
  const currentScopeVersion = connector?.scopeVersion
  const companyScopeVersion = companyConnector?.scopeVersion
  const hasVersionMismatch = !isConnectorVersionUpToDate(
    connector,
    companyConnector,
  )

  return {
    currentScopeVersion,
    companyScopeVersion,
    hasVersionMismatch,
  }
}
